import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { Box, Select, MenuItem, Typography, Snackbar } from '@mui/material';
import './Clients.css';

const addicon = apiService.getBlobAssetUrl('Add_Icon.svg');
const search = apiService.getBlobAssetUrl('search.svg');
const editIcon = apiService.getBlobAssetUrl('edit_12.svg');
const DeleteIcon = apiService.getBlobAssetUrl('delete_A1.svg');

const Clients = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showManageContactsModal, setShowManageContactsModal] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [existingContacts, setExistingContacts] = useState([]);
  const [newContacts, setNewContacts] = useState([]);
  const [newClient, setNewClient] = useState({ name: '', owners: [{ name: '', email: '' }] });
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddFormChanged, setIsAddFormChanged] = useState(false);
  const [isEditFormChanged, setIsEditFormChanged] = useState(false);
  const [emailErrors, setEmailErrors] = useState({});
  const [nameErrors, setNameErrors] = useState({});
  const [ownersToDelete, setOwnersToDelete] = useState([]);
  const [originalContacts, setOriginalContacts] = useState([]);

  const moduleAccess = JSON.parse(sessionStorage.getItem('module_access') || '{}');
  const hasWriteAccess = moduleAccess.Clients === 'Read/Write';

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.com$/i;
    return emailRegex.test(email);
  };

  const validateName = (name) => {
    if (!name.trim()) {
      return false; // Empty or only spaces
    }
    const nameRegex = /^[a-zA-Z0-9 ]*$/; // Letters, numbers, spaces
    return nameRegex.test(name);
  };

  const checkDuplicateEmail = (owners, currentIndex, value, additionalOwners = []) => {
    const allOwners = [...owners, ...additionalOwners];
    return allOwners.some((owner, index) =>
      index !== currentIndex && owner.email.toLowerCase() === value.toLowerCase()
    );
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const clientsData = await apiService.fetchData('clients');
        const safeClientsData = Array.isArray(clientsData) ? clientsData : [];
        const updatedClients = safeClientsData.map(client => ({
          ...client,
          OwnerNames: Array.isArray(client.Owners)
            ? client.Owners.map(o => o.owner_name || '').join(',')
            : client.OwnerNames || '',
          OwnerEmails: Array.isArray(client.Owners)
            ? client.Owners.map(o => (o.owner_email || '').toLowerCase()).join(',')
            : (client.OwnerEmails || '').toLowerCase(),
        }));
        setClients(updatedClients);
        setFilteredClients(updatedClients);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch clients data');
        setLoading(false);
        setSnackbarMessage('Failed to fetch clients data');
        setSnackbarOpen(true);
      }
    };

    fetchClients();
  }, []);

  useEffect(() => {
    const filtered = clients.filter((client) =>
      (client.Name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredClients(filtered);
    setCurrentPage(1);
  }, [searchTerm, clients]);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentClients = filteredClients.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleManageContactsClick = async (client) => {
    setSelectedClient(client);
    setEmailErrors({});
    setNameErrors({});
    setIsEditFormChanged(false);
    try {
      const clientOwnersData = await apiService.fetchData(`clients/${client.Name}`);
      const existing = Array.isArray(clientOwnersData) ? clientOwnersData.map(o => ({
        Id: o.ID,
        name: o.OwnerName || '',
        email: (o.OwnerEmail || '').toLowerCase(),
      })) : [];
      setExistingContacts(existing);
      setOriginalContacts(JSON.parse(JSON.stringify(existing)));
    } catch (err) {
      console.error('Failed to fetch client owners:', err);
      setSnackbarMessage('Failed to fetch client owners');
      setSnackbarOpen(true);
      setExistingContacts([]);
      setOriginalContacts([]);
    }
    setNewContacts([]);
    setOwnersToDelete([]);
    setShowManageContactsModal(true);
  };

  const handleExistingContactChange = (index, field, value) => {
    const updated = [...existingContacts];
    updated[index][field] = field === 'email' ? value.toLowerCase() : value;
    setExistingContacts(updated);
    setIsEditFormChanged(true);

    if (field === 'email') {
      setEmailErrors(prev => ({
        ...prev,
        [`existing-${index}`]: value && !validateEmail(value)
          ? 'Please enter a valid email address (e.g., user@example.com)'
          : checkDuplicateEmail([...existingContacts, ...newContacts], index, value.toLowerCase())
          ? 'This email is already used'
          : '',
      }));
    } else if (field === 'name') {
      setNameErrors(prev => ({
        ...prev,
        [`existing-${index}`]: value && !validateName(value)
          ? 'Name can only contain letters, numbers, and spaces (cannot be empty or only spaces)'
          : '',
      }));
    }
  };

  const handleNewContactChange = (index, field, value) => {
    const updated = [...newContacts];
    updated[index][field] = field === 'email' ? value.toLowerCase() : value;
    setNewContacts(updated);
    setIsEditFormChanged(true);

    if (field === 'email') {
      setEmailErrors(prev => ({
        ...prev,
        [`new-${index}`]: value && !validateEmail(value)
          ? 'Please enter a valid email address (e.g., user@example.com)'
          : checkDuplicateEmail([...existingContacts, ...newContacts], existingContacts.length + index, value.toLowerCase())
          ? 'This email is already used'
          : '',
      }));
    } else if (field === 'name') {
      setNameErrors(prev => ({
        ...prev,
        [`new-${index}`]: value && !validateName(value)
          ? 'Name can only contain letters, numbers, and spaces (cannot be empty or only spaces)'
          : '',
      }));
    }
  };

  const handleNewClientChange = (field, value, index = null) => {
    if (field === 'name') {
      setNewClient(prev => ({ ...prev, name: value }));
      setNameErrors(prev => ({
        ...prev,
        'new-client': value && !validateName(value)
          ? 'Client name can only contain letters, numbers, and spaces (cannot be empty or only spaces)'
          : '',
      }));
    } else if (field === 'ownerName' && index !== null) {
      const updatedOwners = [...newClient.owners];
      updatedOwners[index].name = value;
      setNewClient(prev => ({ ...prev, owners: updatedOwners }));
      setNameErrors(prev => ({
        ...prev,
        [`new-client-owner-${index}`]: value && !validateName(value)
          ? 'Owner name can only contain letters, numbers, and spaces (cannot be empty or only spaces)'
          : '',
      }));
    } else if (field === 'ownerEmail' && index !== null) {
      const updatedOwners = [...newClient.owners];
      updatedOwners[index].email = value.toLowerCase();
      setNewClient(prev => ({ ...prev, owners: updatedOwners }));
      setEmailErrors(prev => ({
        ...prev,
        [`new-client-owner-${index}`]: value && !validateEmail(value)
          ? 'Please enter a valid email address (e.g., user@example.com)'
          : checkDuplicateEmail(newClient.owners, index, value.toLowerCase())
          ? 'This email is already used'
          : '',
      }));
    }
    setIsAddFormChanged(true);
  };

  const handleAddNewClientOwner = () => {
    setNewClient(prev => ({
      ...prev,
      owners: [...prev.owners, { name: '', email: '' }],
    }));
  };

  const handleDeleteNewClientOwner = (index) => {
    const updatedOwners = newClient.owners.filter((_, i) => i !== index);
    setNewClient(prev => ({
      ...prev,
      owners: updatedOwners.length > 0 ? updatedOwners : [{ name: '', email: '' }],
    }));
    setIsAddFormChanged(true);
    setEmailErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`new-client-owner-${index}`];
      return newErrors;
    });
    setNameErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`new-client-owner-${index}`];
      return newErrors;
    });
  };

  const handleDeleteExistingContact = (index) => {
    if (existingContacts.length <= 1 && newContacts.length === 0) return;
    const contactToDelete = existingContacts[index];
    if (contactToDelete.Id) {
      setOwnersToDelete([...ownersToDelete, contactToDelete.Id]);
    }
    setExistingContacts(existingContacts.filter((_, i) => i !== index));
    setIsEditFormChanged(true);
    setEmailErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`existing-${index}`];
      return newErrors;
    });
    setNameErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`existing-${index}`];
      return newErrors;
    });
  };

  const handleDeleteNewContact = (index) => {
    setNewContacts(newContacts.filter((_, i) => i !== index));
    setIsEditFormChanged(true);
    setEmailErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`new-${index}`];
      return newErrors;
    });
    setNameErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`new-${index}`];
      return newErrors;
    });
  };

  const handleSaveContacts = async () => {
    const allContacts = [...existingContacts, ...newContacts];
    if (!selectedClient || !allContacts.every(c => c.name && c.email)) {
      setSnackbarMessage('Please provide both name and email for all contacts.');
      setSnackbarOpen(true);
      return;
    }

    if (Object.values(emailErrors).some(error => error) || Object.values(nameErrors).some(error => error)) {
      setSnackbarMessage('Please correct invalid fields.');
      setSnackbarOpen(true);
      return;
    }

    setIsEditing(true);

    try {
      const userId = sessionStorage.getItem('user_id');
      const accessToken = process.env.REACT_APP_ACCESS_TOKEN;

      // Update existing contacts
      if (existingContacts.length > 0) {
        const updatePayload = existingContacts.map((contact) => ({
          Id: contact.Id,
          owner_name: contact.name,
          owner_email: contact.email.toLowerCase(),
          created_by: userId ? parseInt(userId) : null,
        }));
        await apiService.putData('clients', updatePayload, accessToken);
      }

      // Add new contacts
      if (newContacts.length > 0) {
        const addPayload = newContacts.map((contact) => ({
          name: selectedClient.Name,
          owner_name: contact.name,
          owner_email: contact.email.toLowerCase(),
          created_by: userId ? parseInt(userId) : null,
        }));
        await apiService.postData('clients', addPayload, accessToken);
      }

      // Delete removed contacts
      if (ownersToDelete.length > 0) {
        const deletePayload = { ids: ownersToDelete };
        await apiService.deleteData('clients', deletePayload, accessToken);
      }

      setSnackbarMessage('Contacts updated successfully!');

      const updatedClientsData = await apiService.fetchData('clients');
      const syncedClients = (Array.isArray(updatedClientsData) ? updatedClientsData : []).map(client => ({
        ...client,
        OwnerNames: Array.isArray(client.Owners) ? client.Owners.map(o => o.owner_name || '').join(',') : client.OwnerNames || '',
        OwnerEmails: Array.isArray(client.Owners) ? client.Owners.map(o => (o.owner_email || '').toLowerCase()).join(',') : (client.OwnerEmails || '').toLowerCase(),
      }));
      setClients(syncedClients);
      setFilteredClients(syncedClients);

      setSnackbarOpen(true);
      setShowManageContactsModal(false);
      setExistingContacts([]);
      setNewContacts([]);
      setOriginalContacts([]);
      setOwnersToDelete([]);
      setIsEditFormChanged(false);
      setEmailErrors({});
      setNameErrors({});
    } catch (err) {
      console.error('Failed to update contacts:', err);
      setSnackbarMessage(err.message || 'Failed to update contacts.');
      setSnackbarOpen(true);
    } finally {
      setIsEditing(false);
    }
  };

  const handleSaveNewClient = async () => {
    if (!newClient.name || !newClient.owners.every(owner => owner.name && owner.email)) {
      setSnackbarMessage('Please provide client name and both owner name and email for all owners.');
      setSnackbarOpen(true);
      return;
    }

    if (Object.values(emailErrors).some(error => error) || Object.values(nameErrors).some(error => error)) {
      setSnackbarMessage('Please correct invalid fields.');
      setSnackbarOpen(true);
      return;
    }

    setIsAdding(true);

    try {
      const userId = sessionStorage.getItem('user_id');
      if (!userId) {
        console.warn('userId not found in session storage, using null');
      }
      const accessToken = process.env.REACT_APP_ACCESS_TOKEN;

      const payload = newClient.owners.map((owner) => ({
        name: newClient.name,
        owner_name: owner.name,
        owner_email: owner.email.toLowerCase(),
        created_by: userId ? parseInt(userId) : null,
      }));

      await apiService.postData('clients', payload, accessToken);
      setSnackbarMessage('Client added successfully!');

      const updatedClientsData = await apiService.fetchData('clients');
      const syncedClients = (Array.isArray(updatedClientsData) ? updatedClientsData : []).map(client => ({
        ...client,
        OwnerNames: Array.isArray(client.Owners) ? client.Owners.map(o => o.owner_name || '').join(',') : client.OwnerNames || '',
        OwnerEmails: Array.isArray(client.Owners) ? client.Owners.map(o => (o.owner_email || '').toLowerCase()).join(',') : (client.OwnerEmails || '').toLowerCase(),
      }));
      setClients(syncedClients);
      setFilteredClients(syncedClients);

      setSnackbarOpen(true);
      setShowAddClientModal(false);
      setNewClient({ name: '', owners: [{ name: '', email: '' }] });
      setIsAddFormChanged(false);
      setEmailErrors({});
      setNameErrors({});
    } catch (err) {
      console.error('Failed to add client:', err);
      setSnackbarMessage(err.message || 'Failed to add client.');
      setSnackbarOpen(true);
    } finally {
      setIsAdding(false);
    }
  };



  return (
    <Box className="clients-page">
      <div className="clients-header">
        <h1 className="clients-header-h1">Clients</h1>
        {hasWriteAccess && (
          <Typography
            className="clients-add-new-client"
            onClick={() => {
              setShowAddClientModal(true);
              setNewClient({ name: '', owners: [{ name: '', email: '' }] });
              setEmailErrors({});
              setNameErrors({});
              setIsAddFormChanged(false);
            }}
            role="button"
            aria-label="Add New Client"
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <img src={addicon} alt="Add New Client" style={{ width: '20px', height: '20px', marginRight: '8px' }} />
            Add New Client
          </Typography>
        )}
      </div>
      <div className="clients-header-actions">
        <div className="survey-search-wrapper">
          <input
            type="text"
            className="survey-search"
            placeholder="Search here..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            title="Search here..."
          />
          <img src={search} alt="Search" className="search-img" />
        </div>
        <div className="clients-actions">
          <Box>
            <Typography id="resultssurvey">
              Results: {filteredClients.length === 0 ? 0 : indexOfFirst + 1}-{Math.min(indexOfLast, filteredClients.length)} of {filteredClients.length}
            </Typography>
          </Box>
          <select
            className="survey-items-per-page-select"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
          >
            {[5, 10, 15, 20].map((count) => (
              <option key={count} value={count}>{count}</option>
            ))}
          </select>
        </div>
      </div>

      <br />

      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div>{error}</div>
      ) : (
        <table className="clients-table">
          <thead>
            <tr>
              <th>Client Name</th>
              <th>Client Sponsor Name</th>
              <th>Email ID</th>
              {hasWriteAccess && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {currentClients.length > 0 ? (
              currentClients.map((client, clientIndex) => (
                <tr key={clientIndex}>
                  <td className={client.Name ? '' : 'na-text'}>{client.Name || 'N/A'}</td>
                  <td className={client.OwnerNames ? '' : 'na-text'}>{client.OwnerNames || 'N/A'}</td>
                  <td className={client.OwnerEmails ? '' : 'na-text'}>{client.OwnerEmails || 'N/A'}</td>
                  {hasWriteAccess && (
                    <td className="clients-actions">
                      <img
                        src={editIcon}
                        alt="Manage Contacts"
                        className="action-icon"
                        onClick={() => handleManageContactsClick(client)}
                        title="Manage Contacts"
                      />
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={hasWriteAccess ? 4 : 3}>NO CLIENTS FOUND</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <div className="survey-pagination-container">
        <button
          className="survey-pagination-button"
          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Previous Page"
        >
          &lt;
        </button>
        {(() => {
          const pageButtons = [];
          let startPage = Math.max(1, currentPage - 1);
          let endPage = Math.min(totalPages, startPage + 2);
          if (endPage - startPage < 2) {
            startPage = Math.max(1, endPage - 2);
          }
          for (let page = startPage; page <= endPage; page++) {
            pageButtons.push(
              <button
                key={`page-${page}`}
                className={`survey-pagination-button ${currentPage === page ? 'active' : ''}`}
                onClick={() => handlePageChange(page)}
                aria-label={`Go to page ${page}`}
              >
                {page}
              </button>
            );
          }
          return pageButtons;
        })()}
        <button
          className="survey-pagination-button"
          onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Next Page"
        >
          &gt;
        </button>
      </div>

      {showManageContactsModal && selectedClient && (
        <div className="add-survey-modal-overlay">
          <div className="add-owner-modal-content">
            <div className="modal-header">
              <h2>Manage Contacts for {selectedClient.Name}</h2>
              <span className="modal-close" onClick={() => setShowManageContactsModal(false)}>&times;</span>
            </div>
            <div className="form-container">
              <div className="input-row" style={{ marginBottom: '10px' }}>
                <div className="form-group">
                  <label>Full Name<span className="mandatory-asterisk">*</span></label>
                </div>
                <div className="form-group">
                  <label>Email ID<span className="mandatory-asterisk">*</span></label>
                </div>
              </div>
              {existingContacts.map((contact, index) => (
                <div key={`existing-${index}`} className="owner-group-row">
                  <div className="owner-inputs">
                    <div className="input-row">
                      <div className="form-group">
                        <input
                          type="text"
                          value={contact.name}
                          onChange={(e) => handleExistingContactChange(index, 'name', e.target.value)}
                          placeholder="Enter name"
                          required
                        />
                        {nameErrors[`existing-${index}`] && (
                          <span className="email-error">{nameErrors[`existing-${index}`]}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <input
                          type="email"
                          value={contact.email}
                          onChange={(e) => handleExistingContactChange(index, 'email', e.target.value)}
                          placeholder="Enter email"
                          required
                        />
                        {emailErrors[`existing-${index}`] && (
                          <span className="email-error">{emailErrors[`existing-${index}`]}</span>
                        )}
                      </div>
                      <img
                        src={DeleteIcon}
                        alt="Delete Contact"
                        className={`delete-icon ${existingContacts.length <= 1 && newContacts.length === 0 ? 'disabled' : ''}`}
                        onClick={() => handleDeleteExistingContact(index)}
                        style={existingContacts.length <= 1 && newContacts.length === 0 ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {newContacts.map((contact, index) => (
                <div key={`new-${index}`} className="owner-group-row">
                  <div className="owner-inputs">
                    <div className="input-row">
                      <div className="form-group">
                        <input
                          type="text"
                          value={contact.name}
                          onChange={(e) => handleNewContactChange(index, 'name', e.target.value)}
                          placeholder="Enter name"
                          required
                        />
                        {nameErrors[`new-${index}`] && (
                          <span className="email-error">{nameErrors[`new-${index}`]}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <input
                          type="email"
                          value={contact.email}
                          onChange={(e) => handleNewContactChange(index, 'email', e.target.value)}
                          placeholder="Enter email"
                          required
                        />
                        {emailErrors[`new-${index}`] && (
                          <span className="email-error">{emailErrors[`new-${index}`]}</span>
                        )}
                      </div>
                      <img
                        src={DeleteIcon}
                        alt="Delete Contact"
                        className="delete-icon"
                        onClick={() => handleDeleteNewContact(index)}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <div className="add-owner-button">
                <button
                  onClick={() => setNewContacts([...newContacts, { name: '', email: '' }])}
                >
                  Add Contact
                </button>
              </div>
            </div>
            <div className="modal-buttons">
              <button
                onClick={handleSaveContacts}
                disabled={isEditing || !isEditFormChanged || ![...existingContacts, ...newContacts].every(c => c.name && c.email) || Object.values(emailErrors).some(error => error) || Object.values(nameErrors).some(error => error)}
              >
                {isEditing ? 'Saving' : 'Save'}
              </button>
              <button onClick={() => setShowManageContactsModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showAddClientModal && hasWriteAccess && (
        <div className="add-survey-modal-overlay">
          <div className="add-owner-modal-content">
            <div className="modal-header">
              <h2>Add New Client</h2>
            </div>
            <div className="form-container add-client-top-row" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div className="form-group">
                <label>Client Name:<span className="mandatory-asterisk">*</span></label>
                <input
                  type="text"
                  value={newClient.name}
                  onChange={(e) => handleNewClientChange('name', e.target.value)}
                  placeholder="Enter client name"
                  required
                />
                {nameErrors['new-client'] && (
                  <span className="email-error">{nameErrors['new-client']}</span>
                )}
              </div>
              <div className="input-row" style={{ marginBottom: '5px' }}>
                <div className="form-group">
                  <label>Full Name<span className="mandatory-asterisk">*</span></label>
                </div>
                <div className="form-group">
                  <label>Email ID<span className="mandatory-asterisk">*</span></label>
                </div>
              </div>
              {newClient.owners.map((owner, index) => (
                <div key={index} className="owner-group-row">
                  <div className="owner-inputs">
                    <div className="input-row">
                      <div className="form-group">
                        <input
                          type="text"
                          value={owner.name}
                          onChange={(e) => handleNewClientChange('ownerName', e.target.value, index)}
                          placeholder="Enter name"
                          required
                        />
                        {nameErrors[`new-client-owner-${index}`] && (
                          <span className="email-error">{nameErrors[`new-client-owner-${index}`]}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <input
                          type="email"
                          value={owner.email}
                          onChange={(e) => handleNewClientChange('ownerEmail', e.target.value, index)}
                          placeholder="Enter email"
                          required
                        />
                        {emailErrors[`new-client-owner-${index}`] && (
                          <span className="email-error">{emailErrors[`new-client-owner-${index}`]}</span>
                        )}
                      </div>
                      <img
                        src={DeleteIcon}
                        alt="Delete Owner"
                        className="delete-icon"
                        onClick={() => handleDeleteNewClientOwner(index)}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <div className="add-owner-button">
                <button onClick={handleAddNewClientOwner}>
                  Add Contact
                </button>
              </div>
            </div>
            <div className="modal-buttons">
              <button
                onClick={handleSaveNewClient}
                disabled={isAdding || !isAddFormChanged}
              >
                {isAdding ? 'Saving' : 'Save'}
              </button>
              <button onClick={() => setShowAddClientModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: snackbarMessage.includes('successfully') ? '#4caf50' : '#f44336',
          },
          zIndex: 1400,
        }}
      />
    </Box>
  );
};

export default Clients;